/**
 * The authorization server's sign-in steps, for the page an app's "connect" sends the browser to
 * (`/oauth/interaction/:uid`). The API answers each step in JSON: what to show next, an error, or
 * the URL that resumes the authorization.
 *
 * Every call carries credentials: the API set an interaction cookie on its own host, scoped to
 * this same path there, when it sent the browser here, and that cookie is what ties these calls
 * to the browser that started the flow.
 */
import { apiBase } from './api';

export type InteractionStep =
  | { step: 'sign-in'; client: string }
  | { step: 'code'; email: string }
  | { step: 'consent'; client: string; email: string }
  | { redirectTo: string };

export class InteractionError extends Error {
  /** True when the interaction is gone and the person must start again from the app. */
  readonly expired: boolean;
  constructor(message: string, expired = false) {
    super(message);
    this.expired = expired;
  }
}

async function call(uid: string, action: string | null, body?: Record<string, string>): Promise<InteractionStep> {
  const path = `${apiBase}/oauth/interaction/${encodeURIComponent(uid)}${action ? `/${action}` : ''}`;
  let res: Response;
  try {
    res = await fetch(path, {
      method: action ? 'POST' : 'GET',
      credentials: 'include',
      headers: action ? { 'Content-Type': 'application/json' } : undefined,
      body: action ? JSON.stringify(body ?? {}) : undefined,
    });
  } catch {
    throw new InteractionError('Could not reach the server. Please check your connection and try again.');
  }
  const data = (await res.json().catch(() => null)) as (InteractionStep & { error?: unknown }) | null;
  if (!res.ok || !data) {
    const error = data?.error;
    throw new InteractionError(typeof error === 'string' && error ? error : `Request failed (${res.status}).`, res.status === 410);
  }
  return data;
}

/** Which step this interaction is at. */
export const loadStep = (uid: string): Promise<InteractionStep> => call(uid, null);

/** Mail a code. The answer is the same whether or not the address has an account. */
export const sendCode = (uid: string, email: string): Promise<InteractionStep> => call(uid, 'email', { email });

/** Verify the code; a correct one answers with the URL that continues the authorization. */
export const verifyCode = (uid: string, email: string, code: string): Promise<InteractionStep> =>
  call(uid, 'code', { email, code });

/** Allow the app. */
export const allow = (uid: string): Promise<InteractionStep> => call(uid, 'consent');

/** Refuse the app; the answer sends the browser back to it with access_denied. */
export const refuse = (uid: string): Promise<InteractionStep> => call(uid, 'abort');
